import { ESdChangeReason, IDomainDto } from "@kuika/kuika-cl-model";
import classNames from "classnames";
import { UserService } from "../../../services/user-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../shared/hoc/with-context";
import { IWithHistoryProp, withHistory } from "../../../shared/hoc/with-history";
import useLoadingPresentation, { LoadingPresentation } from "../../../shared/hooks/useLoadingPresentation";
import { PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaEmptyState from "../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaOverflowText from "../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import KuikaSkeleton, { KuikaSkeletonRegion } from "../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSpinner from "../../ui-kit/kuika-spinner/kuika-spinner";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallMediumText from "../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaSmallText from "../../ui-kit/typography/SmallText/SmallText";
import { CreateDomainModal } from "../left-panel/workspace-panel/create-domain-modal";
import "./workspace-switcher.scss";

declare let window: any;

const AUTHORIZED_USERS: string[] =
  window._env_?.AUTHORIZED_USERS?.split(",")
    .map((e: string) => e.trim())
    .filter(Boolean) || [];

/**
 * Initials for a workspace badge: "burcu_yazici_01" -> BY, "Kuika Product Team" -> KP.
 * Splits on the separators workspace names actually use, drops purely numeric tokens
 * (the trailing "_01" is a tenant suffix, not a word) and takes the first two letters.
 * toUpperCase is locale-independent in JS — toLocaleUpperCase would break under tr-TR.
 */
export const getWorkspaceInitials = (name: string): string => {
  const words: string[] = (name ?? "")
    .split(/[^a-zA-Z0-9]+/)
    .filter((word: string) => word.length > 0 && !/^\d+$/.test(word));
  if (words.length === 0) return (name ?? "").slice(0, 2).toUpperCase();
  const letters: string = words
    .slice(0, 2)
    .map((word: string) => word.charAt(0))
    .join("");
  return letters.toUpperCase();
};

/**
 * The single-workspace lock is only correct while that workspace is the one the user is on.
 * After being removed from a workspace the authorized list holds just their own workspace,
 * and the lock used to close the only way out of the workspace they no longer belong to.
 */
export const isWorkspaceSwitcherLocked = (
  domains: IDomainDto[],
  currentDomain: string,
  isPlatformEnv: boolean
): boolean => {
  if (!isPlatformEnv) return false;
  // List not loaded yet (or the request failed) — keep the existing behavior.
  if (domains.length === 0) return true;
  if (domains.length >= 2) return false;

  return domains.some((domain: IDomainDto): boolean => domain.name === currentDomain);
};

/** Name bars that end at different places, so the rows read as names and not as stripes. */
const WORKSPACE_SKELETON_NAME_WIDTHS: readonly string[] = ["64%", "48%", "72%", "56%"];

export interface IWorkspaceListPlaceholderProps {
  isLoading: boolean;
}

/**
 * What the menu shows while it has no workspace rows: skeleton rows while the list is still on
 * its way — it used to say "cannot be found" about a list that simply had not arrived — and the
 * empty state once it has and nothing matches. A function component so the class switcher gets
 * the loading gate's show delay (useLoadingPresentation). The rows keep the real ones' shape —
 * height, badge, the unchecked rows' gutter — so nothing jumps when they arrive.
 */
export const WorkspaceListPlaceholder = ({ isLoading }: IWorkspaceListPlaceholderProps): JSX.Element | null => {
  const presentation: LoadingPresentation = useLoadingPresentation({ isLoading, hasData: false });

  if (presentation === "pending") {
    return null;
  }

  if (presentation === "skeleton") {
    return (
      <KuikaSkeletonRegion label="Loading workspaces" className="kuika-workspace-switcher__skeleton">
        {WORKSPACE_SKELETON_NAME_WIDTHS.map((width: string): JSX.Element => (
          <div key={width} className="kuika-workspace-switcher__skeleton-row">
            <KuikaSkeleton width={24} height={24} className="kuika-workspace-switcher__skeleton-badge" />
            <KuikaSkeleton width={width} height="var(--gap-m)" />
          </div>
        ))}
      </KuikaSkeletonRegion>
    );
  }

  return (
    <KuikaEmptyState
      svgName="briefcase"
      name="Workspace"
      description="Workspace that you’re looking for cannot be found."
    />
  );
};

export interface IWorkspaceSwitcherState {
  searchText: string;
  isCreateDomainModalActive: boolean;
  domains: IDomainDto[];
  /** Until the authorized list first answers, successfully or not. */
  isDomainsLoading: boolean;
  isMenuOpen: boolean;
  isLoading: boolean;
}

export interface IWorkspaceSwitcherProps {}

type WorkspaceSwitcherAllProps = IWorkspaceSwitcherProps & IWithSDProps & IWithContextProp & IWithHistoryProp;

/**
 * Top-bar workspace switcher. Moved out of the left panel so the shell carries one
 * identity cluster (workspace + account) instead of two.
 */
class WorkspaceSwitcher extends PureSdComponent<WorkspaceSwitcherAllProps, IWorkspaceSwitcherState> {
  constructor(props: WorkspaceSwitcherAllProps) {
    super(props);
    this.state = {
      searchText: "",
      isCreateDomainModalActive: false,
      domains: [],
      isDomainsLoading: true,
      isMenuOpen: false,
      isLoading: false
    };
  }

  componentDidMount = (): void => {
    UserService.GetAuthorizedDomains().then(
      (res) => {
        if (res && res.status === 200) {
          this.setState({ domains: res.data, isDomainsLoading: false });
        } else {
          this.setState({ isDomainsLoading: false });
        }
      },
      (error) => {
        this.setState({ isDomainsLoading: false });
        const errorMessage: string | undefined = KMainFunctions.getApiErrorMessage(error);
        if (errorMessage) KMainFunctions.displayErrorNotification(errorMessage);
      }
    );
  };

  private openCreateDomainModal = (): void => this.setState({ isCreateDomainModalActive: true, isMenuOpen: false });

  private closeCreateDomainModal = (): void => this.setState({ isCreateDomainModalActive: false });

  private selectDomain = async (domainName: string): Promise<void> => {
    this.setState({ isLoading: true });
    try {
      await UserService.ChangeDomain(domainName);
      this.props.navigator.setCurrentDomain(domainName);
    } catch (error) {
      // Only a failed switch takes the logo down — it used to stay up for good. A successful one
      // keeps it until the page reloads into the new workspace; the switch itself is unchanged.
      this.setState({ isLoading: false });
      throw error;
    }
  };

  private getCurrentDomain = (): string => this.props.navigator.getCurrentDomain();

  /**
   * A tenant that owns exactly one workspace on the hosted platform has nothing to switch
   * to, so the control stays a plain label — unless that one workspace is not the one they
   * are currently on, which is how a removed member gets back (see isWorkspaceSwitcherLocked).
   */
  private getIsSingleDomain = (): boolean => {
    const hostname: string = window?.location?.hostname ?? "";
    const isPlatformEnv: boolean = hostname === "platform.kuika.com" || hostname === "preprod.kuika.com";
    return isWorkspaceSwitcherLocked(this.state.domains, this.getCurrentDomain(), isPlatformEnv);
  };

  private getFilteredDomains = (): IDomainDto[] => {
    const searchText: string = this.state.searchText.toLowerCase();
    return this.state.domains
      .filter((domain: IDomainDto) => domain.name.toLowerCase().includes(searchText))
      .sort((a: IDomainDto, b: IDomainDto) => a.name.localeCompare(b.name));
  };

  private renderBadge = (name: string): JSX.Element => (
    <span className="kuika-workspace-switcher__badge">{getWorkspaceInitials(name)}</span>
  );

  private getMenuOptions = (): KuikaDropdownMenuItemType[] =>
    this.getFilteredDomains().map((domain: IDomainDto) => ({
      key: domain.name,
      label: domain.name,
      dataTestId: `workspaceOption-${domain.name}`,
      icon: this.renderBadge(domain.name)
    }));

  private renderHeader = (): JSX.Element => (
    <KuikaInput
      data-testid="searchWorkspaceInput"
      data-onboarding-id="management_workspace_search-input"
      size="sm"
      placeholder="Search workspace..."
      value={this.state.searchText}
      onChange={(e) => this.setState({ searchText: e.target.value })}
      leftIcon={<KuikaSvg name="search" width={14} height={14} />}
    />
  );

  private renderFooter = (): JSX.Element | null => {
    if (!AUTHORIZED_USERS.includes(this.props.context?.userInfo?.username)) return null;

    return (
      <button
        type="button"
        data-testid="createWorkspaceButton"
        className="kuika-workspace-switcher__create"
        onClick={this.openCreateDomainModal}
      >
        <KuikaSvg name="plus" width={16} height={16} />
        <span>Create a new workspace</span>
      </button>
    );
  };

  render() {
    const { isMenuOpen, isCreateDomainModalActive } = this.state;
    const isSingleDomain: boolean = this.getIsSingleDomain();
    const currentDomain: string = this.getCurrentDomain();

    return (
      <>
        <KuikaSpinner size="large" spinning={this.state.isLoading} />
        {isCreateDomainModalActive && (
          <CreateDomainModal closeModal={this.closeCreateDomainModal} selectDomain={this.selectDomain} />
        )}
        <KuikaDropdownMenu
          options={this.getMenuOptions()}
          emptyState={<WorkspaceListPlaceholder isLoading={this.state.isDomainsLoading} />}
          emptyStateCondition={this.getMenuOptions().length === 0}
          open={isMenuOpen}
          setOpen={(value: boolean) => this.setState({ isMenuOpen: isSingleDomain ? false : value })}
          selected={currentDomain}
          setSelected={(key: number | string | null) => this.selectDomain(String(key))}
          // The switcher sits on the header's LEFT now — the menu grows rightward from the
          // trigger's left edge instead of extending toward (and past) the viewport edge.
          placement="bottomLeft"
          overlayClassName="kuika-workspace-switcher__overlay"
          menuTestId="workspaceDropdownMenu"
          header={this.renderHeader()}
          footer={this.renderFooter()}
        >
          {/* A real button: Tab reaches it, Enter and Space open it, and a click leaves it holding
              focus. From an unfocusable div the menu's own focus hand-off counted as keyboard
              focus, and a pointer-opened menu drew the focus ring on its current row. */}
          <button
            type="button"
            data-testid="workspaceDropdown"
            data-onboarding-id="management_workspace_dropdown"
            aria-haspopup="menu"
            aria-expanded={isMenuOpen}
            aria-disabled={isSingleDomain || undefined}
            className={classNames("kuika-workspace-switcher__trigger", OVERFLOW_REVEAL_HOST_CLASS, {
              "kuika-workspace-switcher__trigger--static": isSingleDomain,
              "kuika-workspace-switcher__trigger--open": isMenuOpen
            })}
          >
            <span className="kuika-workspace-switcher__labels">
              <KuikaSmallText className="kuika-workspace-switcher__caption">Workspace</KuikaSmallText>
              <KuikaSmallMediumText
                data-testid="currentWorkspaceName"
                data-onboarding-id="management_workspace_current-name"
                className="kuika-workspace-switcher__name"
              >
                <KuikaOverflowText>{currentDomain}</KuikaOverflowText>
              </KuikaSmallMediumText>
            </span>
            {/* Up-down selector glyph: reads as "switch between options", not "expand".
                It stays static while open — the open menu is its own feedback. */}
            {!isSingleDomain && <KuikaSvg type="arrows" name="chevron-selector" width={16} height={16} pointer />}
          </button>
        </KuikaDropdownMenu>
      </>
    );
  }

  checkNeedRender = (_: ESdChangeReason): boolean => false;
}

const workspaceSwitcher = withContext<IWorkspaceSwitcherProps>(
  withHistory<IWorkspaceSwitcherProps>(withSD<IWorkspaceSwitcherProps>(WorkspaceSwitcher))
);
export { workspaceSwitcher as WorkspaceSwitcher };
